import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { dispatcherService } from '@/api/services/dispatcherService'
import { precinctService } from '@/api/services/precinctService'
import { MUTATION_KEYS, QUERY_KEYS } from '@/config/api'
import type {
  UpdateDispatcherActivePrecinctPayload,
  UpdateDispatcherProfilePayload,
} from '@/types/api/dispatcher'
import { createApiRetryHandler } from '@/utils/apiRetry'

export const useGetAllDispatchers = () => {
  return useQuery({
    queryKey: ['dispatchers'],
    queryFn: () => dispatcherService.getAllDispatchers(),
    staleTime: 10 * 60 * 1000, // 10 minutes - data is fresh for 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes - cache for 30 minutes after becoming stale
    retry: createApiRetryHandler(5),
  })
}

export const useGetDispatcher = (dispatcherId: string) => {
  return useQuery({
    queryKey: ['dispatchers', dispatcherId],
    queryFn: () => dispatcherService.getDispatcher(dispatcherId),
    enabled: !!dispatcherId,
    staleTime: 10 * 60 * 1000, // 10 minutes - data is fresh for 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes - cache for 30 minutes after becoming stale
    retry: createApiRetryHandler(5),
  })
}

export const useGetPrecincts = () => {
  return useQuery({
    queryKey: QUERY_KEYS.precincts.all,
    queryFn: () => precinctService.getPrecincts(),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: createApiRetryHandler(3),
  })
}

export const useUpdateDispatcherProfile = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [MUTATION_KEYS.UPDATE_DISPATCHER, 'profile'],
    mutationFn: ({
      dispatcherId,
      payload,
    }: {
      dispatcherId: string
      payload: UpdateDispatcherProfilePayload
    }) => dispatcherService.updateDispatcher(dispatcherId, payload),
    onSuccess: (response, { dispatcherId }) => {
      queryClient.setQueryData(['dispatchers', dispatcherId], response)
      queryClient.setQueryData(
        QUERY_KEYS.dispatchers.detail(dispatcherId),
        response?.data?.dispatcher
      )
      queryClient.setQueryData(
        QUERY_KEYS.dispatchers.detail(`${dispatcherId}-data`),
        response?.data?.dispatcher
      )

      queryClient.invalidateQueries({ queryKey: ['dispatchers'] })
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.dispatchers.details(),
      })

      toast.success('Profile updated successfully')
    },
    onError: (error: any) => {
      console.error('Failed to update dispatcher profile:', error)
      toast.error(error.message || 'Failed to update profile')
    },
  })
}

export const useSwitchDispatcherActivePrecinct = () => {
  return useMutation({
    mutationKey: [MUTATION_KEYS.UPDATE_DISPATCHER, 'active-precinct'],
    mutationFn: (payload: UpdateDispatcherActivePrecinctPayload) =>
      dispatcherService.updateActivePrecinct(payload),
    onError: (error: any) => {
      console.error('Failed to switch active precinct:', error)
      toast.error(error.message || 'Failed to switch active precinct')
    },
  })
}
